import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Markdown } from '@/lib/markdown';
import { formatRelative } from '@/lib/date';
import { useAuthStore } from '@features/authentication/store/authStore';
import { useAnnouncementStore } from '../store';
import { AnnouncementPriority } from '../types';

const SHOWN_KEY = 'csc-wms-critical-shown';

function getShown(): number[] {
  try {
    const raw = sessionStorage.getItem(SHOWN_KEY);
    return raw ? (JSON.parse(raw) as number[]) : [];
  } catch {
    return [];
  }
}

function persistShown(ids: number[]) {
  try {
    sessionStorage.setItem(SHOWN_KEY, JSON.stringify(ids));
  } catch {
    /* sessionStorage unavailable — silently ignore */
  }
}

/**
 * Post-login modal that surfaces Critical-priority announcements once per session.
 * Reads from the active announcements list — no separate API call.
 */
export function CriticalAnnouncementModal() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // Subscribe to the underlying array (stable ref between fetches) and derive `critical`
  // via useMemo. Returning a filtered array directly from a Zustand selector triggers an
  // infinite render loop because .filter() returns a new ref each call.
  const activeAnnouncements = useAnnouncementStore((s) => s.activeAnnouncements);
  const critical = useMemo(
    () => activeAnnouncements.filter((a) => a.priority === AnnouncementPriority.Critical),
    [activeAnnouncements]
  );

  const [open, setOpen] = useState(false);
  const [displayedIds, setDisplayedIds] = useState<number[]>([]);

  // Identify any critical announcements not yet shown this session.
  const unshown = useMemo(() => {
    if (!isAuthenticated) return [];
    const shown = getShown();
    return critical.filter((a) => !shown.includes(a.id));
  }, [critical, isAuthenticated]);

  useEffect(() => {
    if (unshown.length > 0 && !open) {
      setDisplayedIds(unshown.map((a) => a.id));
      setOpen(true);
    }
  }, [unshown, open]);

  function handleDismiss() {
    const shown = new Set([...getShown(), ...displayedIds]);
    persistShown(Array.from(shown));
    setOpen(false);
  }

  // Render the snapshot of items captured when the modal opened, so updates
  // to the active list mid-session don't shift the UI under the user.
  const itemsToRender = critical.filter((a) => displayedIds.includes(a.id));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleDismiss()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Critical {itemsToRender.length === 1 ? 'announcement' : 'announcements'}
          </DialogTitle>
          <DialogDescription>
            Please review {itemsToRender.length === 1 ? 'this notice' : 'these notices'} before continuing.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {itemsToRender.map((a) => (
            <article
              key={a.id}
              className="rounded-lg border border-destructive/30 bg-destructive/5 p-4"
            >
              <h3 className="text-base font-semibold leading-tight">{a.title}</h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {formatRelative(a.publishAt ?? a.createdAt)}
              </p>
              <div className="mt-3">
                <Markdown>{a.content}</Markdown>
              </div>
            </article>
          ))}
        </div>

        <DialogFooter>
          <Button onClick={handleDismiss}>I've read this</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
